import { z } from "zod";

import { ClientSchema } from "@fartherfinance/frontend/api/Entity/requests/getAdvisorClients";
import getAdvisorClientsV2 from "@fartherfinance/frontend/api/Entity/requests/getAdvisorClientsV2";
import getFartherEmployees, {
  Advisor,
} from "@fartherfinance/frontend/api/Entity/requests/getFartherEmployees";
import {
  AdvisorId,
  AdvisorRequestConfig,
} from "@fartherfinance/frontend/api/Types";

import { mapPersonToOption } from "../../../../utils";
import { cache5Min } from "../../core/cache";
import {
  EnumOption,
  isResourceTypeWithValue,
  ResourceTypeResponse,
} from "../../core/types";

// Helper to determine if a kind array or string includes a specific value
function hasKind(
  kindValue: string[] | string | undefined,
  targetKind: string
): boolean {
  if (!kindValue) {
    return false;
  }

  if (Array.isArray(kindValue)) {
    return kindValue.includes(targetKind);
  }

  return kindValue === targetKind;
}

// Function to fetch clients for a specific advisor
async function getClients(advisorId: AdvisorId, config: AdvisorRequestConfig) {
  return cache5Min(`clients-${advisorId}`, async () => {
    try {
      const clients = await getAdvisorClientsV2(advisorId, {
        jwt: config.jwt,
        advisorId: config.advisorId,
      });
      return clients || { clients: [] };
    } catch (error) {
      console.error("[FormEngine:Person] Error fetching clients:", error);
      return { clients: [] };
    }
  });
}

// Function to fetch all Farther employees (includes more than just advisors)
async function getAllEmployees(config: AdvisorRequestConfig) {
  return cache5Min("employees", async () => {
    try {
      const employees = await getFartherEmployees("All", {
        jwt: config.jwt,
        advisorId: config.advisorId,
      });
      return employees || { advisors: [] };
    } catch (error) {
      console.error("[FormEngine:Person] Error fetching employees:", error);
      return { advisors: [] };
    }
  });
}

// Main people fetcher that handles different person kinds (Client, Advisor, Assignee)
export const fetchPeople = async (
  requestConfig: AdvisorRequestConfig,
  values: ResourceTypeResponse[]
): Promise<EnumOption[]> => {
  // Type guard to ensure we have valid resource values
  if (
    !Array.isArray(values) ||
    !values.every((value) => isResourceTypeWithValue(value))
  ) {
    console.warn("[FormEngine:Person] Invalid resource values provided");
    return [];
  }

  try {
    // Check if we need to fetch clients or advisors based on 'kind' value or resource type
    const hasClientKind = values.some(
      (v) =>
        isResourceTypeWithValue(v) &&
        (hasKind(v.kind, "Client") || v.type === "Client")
    );

    const hasAdvisorKind = values.some(
      (v) =>
        isResourceTypeWithValue(v) &&
        (hasKind(v.kind, "Advisor") ||
          v.type === "Assignee" ||
          v.type === "Person")
    );

    // If we have different kinds, we need to fetch both clients and advisors
    let clients: z.infer<typeof ClientSchema>[] = [];
    let advisors: Advisor[] = [];
    let employeeData: { advisors: Advisor[] } = { advisors: [] };

    // Fetch data in parallel for efficiency
    const fetchPromises: Promise<void>[] = [];

    if (hasClientKind) {
      fetchPromises.push(
        getClients(requestConfig.advisorId, requestConfig).then((data) => {
          clients = data.clients || [];
        })
      );
    }

    if (hasAdvisorKind) {
      fetchPromises.push(
        getAllEmployees(requestConfig).then((data) => {
          employeeData = data;
          advisors = data.advisors || [];
        })
      );
    }

    // Wait for all data to be fetched
    await Promise.all(fetchPromises);

    // Create maps for quick lookup
    const clientMap = new Map(
      clients.map((client) => [String(client.clientId), client])
    );

    const advisorMap = new Map(
      advisors.map((advisor) => [String(advisor.advisorId), advisor])
    );

    // Process resources in original order and map to options
    return values
      .filter(isResourceTypeWithValue)
      .map((resource) => {
        const resourceKind = Array.isArray(resource.kind)
          ? resource.kind
          : resource.kind
          ? [resource.kind as string]
          : [];

        // Handle client resources (type Person with kind Client OR type Client)
        if (resourceKind.includes("Client") || resource.type === "Client") {
          const client = clientMap.get(String(resource.value));
          if (client) {
            const clientOption = mapPersonToOption({
              ...client,
              id: client.clientId,
            });
            return {
              type: resource.type,
              kind: resource.kind,
              label: clientOption.label,
              value: String(clientOption.value),
            };
          }
        }

        // Handle advisor resources (type Person with kind Advisor OR type Assignee)
        if (resourceKind.includes("Advisor") || resource.type === "Assignee") {
          const advisor = advisorMap.get(String(resource.value));
          if (advisor) {
            return {
              label: mapPersonToOption({ ...advisor, id: advisor.advisorId })
                .label,
              value: String(advisor.advisorId),
              type: resource.type === "Assignee" ? "Assignee" : resource.type,
              kind: resource.kind,
            };
          }
        }

        // Special case for "creator-person" as a default value type
        if (resource.type === "creator-person") {
          const currentAdvisor = employeeData.advisors.find(
            (a) => a.advisorId === requestConfig.advisorId
          );

          if (currentAdvisor) {
            return {
              label: mapPersonToOption({
                ...currentAdvisor,
                id: currentAdvisor.advisorId,
              }).label,
              value: currentAdvisor.advisorId,
              type: "Person",
              kind: "Advisor",
            };
          }
        }

        // Return null for values that couldn't be mapped to a person
        return null;
      })
      .filter(
        (option): option is NonNullable<typeof option> => option !== null
      );
  } catch (error) {
    console.error("[FormEngine:Person] Error in fetchPeople:", error);
    return [];
  }
};
